import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InputBase from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import { usePersistedDataStore, useLocalDataStore } from '../store.js';
import {useNavigate, useLocation} from 'react-router-dom';
import { toast } from '../../common/toast';
import SvgIconButton from './SvgIconButton';
import logger from '../../utils/logger';

const CompNavigation = ({ handleUpdateStateCallback }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [debounceTimeout, setDebounceTimeout] = useState(null);
    const [pendingCompIndex, setPendingCompIndex] = useState(null);
    const debounceRef = useRef(null);
    const lastValueRef = useRef('');
    const isManualInput = useRef(false);
    const ignoreCompEffect = useRef(false);
    const DEBOUNCE_DELAY = 250;
    const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const getReviewPage = useLocalDataStore((state)=> state.reviewPage)
    const setReviewPage = useLocalDataStore((state)=>state.setReviewPage)
    
    // Safe comp parsing with proper bounds checking
    const compParam = parseInt(searchParams.get('comp'));
    const comp = Number.isFinite(compParam) && compParam > 0 ? compParam - 1 : 0;
    const [virtualCompIndex, setVirtualCompIndex] = useState(comp);
    
    // These indices are set when a filter is applied, use them to restrict the # of comps you display, and to update the comp index to correct number.
    const getFilterIndexes = usePersistedDataStore((state) => state.filterIndexes);
    const getIsFetching = usePersistedDataStore((state) => state.isFetching);
    const processedCount = useLocalDataStore((state) => state.processedCount);
    
    // Calculate the filtered position (1-based) for display
    // virtualCompIndex is the actual index in the original array
    // We need to find its position in the filtered list
    const getFilteredPosition = useCallback((actualIndex) => {
        if (getFilterIndexes && getFilterIndexes.length > 0) {
            const filteredIndex = getFilterIndexes.findIndex(filterIndex => filterIndex === actualIndex);
            return filteredIndex !== -1 ? filteredIndex + 1 : actualIndex + 1; // Return 1-based position
        }
        return actualIndex + 1; // Unfiltered: just return index + 1
    }, [getFilterIndexes]);
    
    // Initialize inputValue with filtered position
    const [inputValue, setInputValue] = useState(() => {
        if (getFilterIndexes && getFilterIndexes.length > 0) {
            const filteredIndex = getFilterIndexes.findIndex(filterIndex => filterIndex === comp);
            return filteredIndex !== -1 ? (filteredIndex + 1).toString() : (comp + 1).toString();
        }
        return (comp + 1).toString();
    });

    // Safe navigation helper with comprehensive bounds checking
    const navigateToComp = useCallback(async (index) => {
        // Guard against invalid index
        if (!Number.isFinite(index) || index < 0) {
            toast.error('Invalid comp index.');
            setInputValue((index + 1).toString());
            return false;
        }

        // Check if negotiation object and cases are available
        if (!getNegotiationObj?.cases?.length) {
            toast.error('Comp data not available.');
            setInputValue((index + 1).toString());
            return false;
        }

        const maxIndex = getNegotiationObj.cases.length - 1;
        
        // Bounds checking
        if (index > maxIndex) {
            toast.error('Comp index exceeds available cases.');
            setInputValue((index + 1).toString());
            return false;
        }

        // Check if the specific case exists
        if (!getNegotiationObj.cases[index]) {
            toast.error('That comp is not available.');
            setInputValue((index + 1).toString());
            return false;
        }

        // Check processed count in unfiltered mode
        if (!getFilterIndexes && index >= processedCount) {
            toast.error('That comp is not available yet.');
            setInputValue((index + 1).toString());
            return false;
        }

        // Filtered mode validation
        if (getFilterIndexes && getFilterIndexes.length !== getNegotiationObj.cases.length) {
            const filteredIndex = getFilterIndexes.findIndex(filterIndex => filterIndex === index);
            if (filteredIndex === -1) {
                toast.error('That comp is not available in current filter.');
                setInputValue((index + 1).toString());
                return false;
            }
        }

        try {
            // Rebuild searchParams to avoid stale closure issues
            const newParams = new URLSearchParams(location.search);
            newParams.set('comp', index + 1);
            
            // Add defensive check for handleUpdateStateCallback
            if (typeof handleUpdateStateCallback === 'function') {
                await handleUpdateStateCallback();
            }
            
            const newUrl = `${location.pathname}?${newParams.toString()}`;
            navigate(newUrl);
            return true;
        } catch (error) {
            logger.error('Navigation error in navigateToComp:', error);
            toast.error('Failed to navigate to comp.');
            setInputValue((index + 1).toString());
            return false;
        }
    }, [getNegotiationObj, getFilterIndexes, processedCount, location, navigate, handleUpdateStateCallback, comp, virtualCompIndex, inputValue]);

    const applyNavigationUpdate = useCallback((nextIndex) => {
        const result = navigateToComp(nextIndex);
        return result;
    }, [navigateToComp]);

    const scheduleDebouncedUpdate = useCallback((nextIndex) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        ignoreCompEffect.current = true;
        debounceRef.current = setTimeout(async () => {
            try {
                await applyNavigationUpdate(nextIndex);
            } catch (error) {
                logger.error('Navigation error in debounce:', error);
                // Reset the flag even if navigation fails
            } finally {
                ignoreCompEffect.current = false;
                debounceRef.current = null;
            }
        }, DEBOUNCE_DELAY);
    }, [applyNavigationUpdate]);

    useEffect(() => {
        setPendingCompIndex(comp);
    }, [comp]);

    // Keep virtualCompIndex in sync when comp changes from URL
    useEffect(() => {
        if (!ignoreCompEffect.current) {
            setVirtualCompIndex(comp);
            // Update input value to show filtered position
            setInputValue(getFilteredPosition(comp));
        }
    }, [comp, getFilteredPosition]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    // Handle input change from InputBase (debounced)
    const handleInputChange = async (event) => {
        const value = event.target.value;
        isManualInput.current = true; // Tell system user is typing manually
        setInputValue(value);
        lastValueRef.current = value;
        
        // Clear any existing timeout
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
            setDebounceTimeout(null);
        }

        // If empty, clear inputValue and don't trigger navigation
        if (value === '') {
            setInputValue('');
            return;
        }

        const timeout = setTimeout(async () => {
            isManualInput.current = false; // Done with manual typing
            
            // Check if input has changed since timeout was scheduled
            if (lastValueRef.current !== value) {
                return;
            }

            const numValue = parseInt(value, 10);
            
            // If not a valid number, don't navigate
            if (!Number.isFinite(numValue)) {
                return;
            }

            // If number is zero or negative, go to first case
            if (numValue <= 0) {
                setInputValue("1");
                await navigateToComp(0);
                return;
            }

            // If number is larger than total cases, go to last case
            if (getNegotiationObj?.cases?.length && numValue > getNegotiationObj.cases.length) {
                const lastIndex = getNegotiationObj.cases.length - 1;
                setInputValue((lastIndex + 1).toString());
                await navigateToComp(lastIndex);
                return;
            }

            // Handle filtered mode
            if (getFilterIndexes && getFilterIndexes.length !== getNegotiationObj.cases.length) {
                if (numValue > getFilterIndexes.length) {
                    toast.error('That comp is not available yet.');
                    setInputValue(getFilteredPosition(virtualCompIndex).toString());
                    return;
                }

                if (numValue < 1) {
                    setInputValue("1");
                    const firstFilteredIndex = getFilterIndexes[0];
                    await navigateToComp(firstFilteredIndex);
                    return;
                }

                const actualIndex = getFilterIndexes[numValue - 1];
                setInputValue(numValue.toString());
                await navigateToComp(actualIndex);
                return;
            }

            // Unfiltered mode
            if (numValue > processedCount) {
                toast.error('That comp is not available yet.');
                setInputValue(getFilteredPosition(virtualCompIndex).toString());
                return;
            }

            if (numValue < 1) {
                setInputValue("1");
                await navigateToComp(0);
                return;
            }

            setInputValue(numValue.toString());
            await navigateToComp(numValue - 1);
        }, 300);

        setDebounceTimeout(timeout);
    };

    // Add blur handler to restore original number if input is invalid/empty
    const handleInputBlur = () => {
        isManualInput.current = false;
        setTimeout(() => {
            const numValue = parseInt(inputValue, 10);
            if (!inputValue || isNaN(numValue)) {
                setInputValue(getFilteredPosition(virtualCompIndex).toString());
            }
        }, 500);
    };

    // REMMEBER YOUR 'comp' is the true index in this component. stupid way to do this differentlyt han others lol
    const handlePrev = useCallback(() => {
        if (getReviewPage) {
            setReviewPage(false);
            return;
        }

        const currentFiltered = getFilterIndexes || [...Array(getNegotiationObj.cases.length).keys()];
        const currentFilteredIndex = currentFiltered.findIndex(i => i === virtualCompIndex);
        const isAtStart = currentFilteredIndex <= 0;

        if (isAtStart) return;

        const nextIndex = currentFiltered[currentFilteredIndex - 1];
        
        // Additional safety check
        if (nextIndex === undefined || nextIndex === null || nextIndex < 0) {
            toast.error('Invalid previous comp.');
            return;
        }
        
        setVirtualCompIndex(nextIndex); // ✅ Local index updates immediately
        setPendingCompIndex(nextIndex);
        setInputValue(getFilteredPosition(nextIndex).toString());
        scheduleDebouncedUpdate(nextIndex);
    }, [virtualCompIndex, getFilterIndexes, getNegotiationObj.cases.length, getReviewPage, setReviewPage, scheduleDebouncedUpdate, comp, getFilteredPosition]);

    const handleNext = useCallback(() => {
        const currentFiltered = getFilterIndexes || [...Array(getNegotiationObj.cases.length).keys()];
        const currentFilteredIndex = currentFiltered.findIndex(i => i === virtualCompIndex);
        
        // Check if we're at the end of available comps
        if (currentFilteredIndex === -1 || currentFilteredIndex >= currentFiltered.length - 1) {
            // If we're at the last processed comp, show review page
            setReviewPage(true);
            return;
        }

        const nextIndex = currentFiltered[currentFilteredIndex + 1];
        
        // Additional safety check to ensure nextIndex is valid
        if (nextIndex === undefined || nextIndex === null) {
            toast.error('No more comps available.');
            return;
        }
        
        // In unfiltered mode, check processed count
        if (!getFilterIndexes && nextIndex >= processedCount) {
            toast.error('That comp is not available yet.');
            return;
        }
        
        setVirtualCompIndex(nextIndex); // ✅ Local index updates immediately
        setPendingCompIndex(nextIndex);
        setInputValue(getFilteredPosition(nextIndex).toString());
        scheduleDebouncedUpdate(nextIndex);
    }, [virtualCompIndex, getFilterIndexes, getNegotiationObj.cases.length, scheduleDebouncedUpdate, setReviewPage, processedCount, comp, getFilteredPosition]);

    // Defensive check for data loading
    if (!getNegotiationObj?.cases?.length) return null;

    // Recalculate position just before render to ensure reactivity
    const currentFiltered = getFilterIndexes || [...Array(getNegotiationObj.cases.length).keys()];
    const currentFilteredIndex = currentFiltered.findIndex(i => i === virtualCompIndex);
    
    // Next button should be enabled if:
    // 1. Not at the end of available comps, OR
    // 2. At the end but not fetching (to allow going to review page)
    // Use filtered length when filtered, otherwise use processedCount or total cases
    const maxAvailableIndex = getFilterIndexes ? getFilterIndexes.length - 1 : (processedCount - 1);
    const isAtEnd = currentFilteredIndex === -1 || currentFilteredIndex >= maxAvailableIndex;
    const isNextDisabled = isAtEnd && getIsFetching;
    
    const isAtStart = currentFilteredIndex <= 0;

    // Can put hover state for the background being gray, but I don't love how the icon looks rounded w/ too large of a bg atm.
    return (
        <div className="flex items-center justify-end">
            <SvgIconButton
                icon={ChevronLeftIcon}
                onClick={handlePrev}
                disabled={isAtStart}
                tooltip="Previous Comp"
                className="p-1"
                activeColor="text.secondary"
                hoverColor="primary.main"
                sx={{ padding: '4px' }}
            />

            {/* MUI InputBase Field for entering the index with minimal styling */}
            <InputBase
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                type="text"
                inputProps={{
                    style: {
                        textAlign: 'center',
                        padding: '0',
                        width: `${inputValue.length + 1}ch`,
                        minWidth: '25px',
                        maxWidth: '40px',
                    },
                }}
                className="text-lg border-none focus:outline-none"
                style={{ fontSize: '1rem', textAlign: 'center', padding: '0', width: 'auto' }}
            />

            <span className="text-lg min-w-[30px]">/ {getFilterIndexes ? getFilterIndexes.length : processedCount}</span>

            {getIsFetching?
            <div className="flex items-center justify-center ml-1">
                <CircularProgress size={18} sx={{ color: 'gray' }}/>
            </div>
            :null}

            <SvgIconButton
                icon={ChevronRightIcon}
                onClick={handleNext}
                disabled={isNextDisabled}
                tooltip="Next Comp"
                className="p-1"
                activeColor="text.secondary"
                hoverColor="primary.main"
                sx={{ padding: '4px' }}
            />
        </div>
    );
};

export default React.memo(CompNavigation);
